/**
 * The runtime the generated Claude Design pages render with (`./generated`, produced by
 * `scripts/design/convert.mjs`).
 *
 * Every rule here mirrors Claude Design's own runtime (`design/claude-design/support.js`) so that a
 * converted page produces the same DOM, the same styles and the same interaction behaviour as the
 * design itself. Change a rule only together with the converter and only to match that runtime.
 */
import { Component, isValidElement, useInsertionEffect, type CSSProperties, type ReactNode } from 'react';

/** Mirrors `StreamableLogic`: page state merges synchronously and re-renders its host. */
export class DesignLogic<P = Record<string, unknown>, S = Record<string, unknown>> {
  props: P;
  state: S = {} as S;
  /** Installed by {@link DesignPage} after construction. */
  __host?: DesignPage;

  constructor(props: P) {
    this.props = props ?? ({} as P);
  }

  setState(update: Partial<S> | ((prev: S) => Partial<S>), cb?: () => void): void {
    this.__host?.setLogicState(update as never, cb);
  }

  forceUpdate(): void {
    this.__host?.forceUpdate();
  }

  componentDidMount(): void {}
  componentDidUpdate(prevProps: P): void {
    void prevProps;
  }
  componentWillUnmount(): void {}

  /** The flat object the template renders against (merged over props). */
  renderVals(): Record<string, unknown> {
    return {};
  }
}

type LogicClass = new (props: Record<string, unknown>) => DesignLogic;
type Template = (vals: Record<string, unknown>) => ReactNode;

interface DesignPageProps {
  Logic: LogicClass;
  render: Template;
  pageProps?: Record<string, unknown>;
  /** The application's own hook, not the design runtime's: called after every update of the page. */
  onUpdate?: () => void;
}

/** Mirrors `StreamableComponent`: owns one logic instance and renders the template inside `.sc-host`. */
export class DesignPage extends Component<DesignPageProps, { v: number }> {
  logic: DesignLogic;

  constructor(props: DesignPageProps) {
    super(props);
    this.state = { v: 0 };
    this.logic = new props.Logic(props.pageProps ?? {});
    this.logic.__host = this;
  }

  setLogicState(update: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>), cb?: () => void) {
    const prev = this.logic.state as Record<string, unknown>;
    const patch = typeof update === 'function' ? update(prev) : update;
    this.logic.state = { ...prev, ...patch };
    this.setState((s) => ({ v: s.v + 1 }), cb);
  }

  override componentDidMount() {
    try {
      this.logic.componentDidMount();
    } catch (e) {
      console.error(e);
    }
  }

  override componentDidUpdate(prevProps: DesignPageProps) {
    this.logic.props = this.props.pageProps ?? {};
    try {
      this.logic.componentDidUpdate(prevProps.pageProps ?? {});
    } catch (e) {
      console.error(e);
    }
    this.props.onUpdate?.();
  }

  override componentWillUnmount() {
    try {
      this.logic.componentWillUnmount();
    } catch (e) {
      console.error(e);
    }
  }

  override render() {
    const userProps = this.props.pageProps ?? {};
    this.logic.props = userProps;
    const vals = { ...userProps, ...(this.logic.renderVals() || {}) };
    return <div className="sc-host">{this.props.render(vals)}</div>;
  }
}

/** Mirrors `cssToObj`: an inline style string becomes a React style object. */
export function css(value: unknown): CSSProperties | undefined {
  if (typeof value !== 'string') return value as CSSProperties | undefined;
  const o: Record<string, string> = {};
  for (const decl of value.split(';')) {
    const i = decl.indexOf(':');
    if (i < 0) continue;
    const prop = decl.slice(0, i).trim();
    o[prop.startsWith('--') ? prop : prop.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())] = decl.slice(i + 1).trim();
  }
  return o as CSSProperties;
}

const HOST_STYLE_PROPS = new Set(['position', 'left', 'right', 'top', 'bottom', 'inset', 'width', 'height', 'z-index', 'transform']);

/** Mirrors `hostPositionStyle`: only positioning properties reach an x-import wrapper. */
export function hostPositionStyle(style: unknown): CSSProperties | undefined {
  const all = typeof style === 'string' ? css(style) : style != null && typeof style === 'object' ? style : null;
  if (!all) return undefined;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(all)) {
    if (HOST_STYLE_PROPS.has(k.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase()))) out[k] = v;
  }
  return Object.keys(out).length ? (out as CSSProperties) : undefined;
}

/** Mirrors `walkText`: an interpolated value renders inside `span.sc-interp`. */
export function interp(v: unknown): ReactNode {
  if (v === undefined || v === null || typeof v === 'boolean') return null;
  if (isValidElement(v) || Array.isArray(v)) return v as ReactNode;
  return <span className="sc-interp">{String(v)}</span>;
}

/** Mirrors `walkFor`: anything that is not an array renders nothing. */
export const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/** Mirrors `walkElement`: an unresolved `value` / `checked` becomes '' / false. */
export const valueOf = (v: unknown) => (v === undefined ? '' : v);
export const checkedOf = (v: unknown) => (v === undefined ? false : v);

/** An x-import this application does not provide (only the Flagship design sheet has one). */
export function MissingImport({ name }: { name: string }) {
  return <div data-missing-import={name} />;
}

/**
 * Where the shared navigation and the pages' links lead. The preview maps design files to preview
 * routes. `search` is a link's own query string (`?create=1`), `''` when it has none.
 */
let hrefResolver = (file: string, device: string, search: string) => `${file}-${device}.dc.html${search}`;
export function setDesignHrefResolver(resolve: (file: string, device: string, search: string) => string) {
  hrefResolver = resolve;
}
export const designHref = (file: string, device: string, search = '') => hrefResolver(file, device, search);

/**
 * The signed-in account the shared navigation names (rail avatar, profile label). Empty in the preview,
 * where the navigation keeps the design's own demo identity.
 */
let identity: { readonly name?: string; readonly initials?: string } = {};
export function setDesignIdentity(next: { readonly name?: string; readonly initials?: string }) {
  identity = next;
}
export const designIdentity = () => identity;

/** Installs a page's <helmet> CSS for as long as the page is mounted. */
export function useDesignCss(cssText: string) {
  useInsertionEffect(() => {
    const el = document.createElement('style');
    el.setAttribute('data-design-page', '');
    el.textContent = cssText;
    document.head.appendChild(el);
    return () => el.remove();
  }, [cssText]);
}
